<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-body pb-10 pt-10 page-container">
          <div v-if="isLoadingTier" class="text-center py-12">
            <b-spinner variant="primary" label="กำลังโหลด..."></b-spinner>
            <span class="text-muted font-weight-bold mt-3 d-block">
              กำลังโหลดข้อมูลระดับสมาชิก...
            </span>
          </div>
          <b-form v-else novalidate @submit.stop.prevent="onSubmit()">
            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">{{ $t("PAGE.DEFAULT.FORM.FIELDS.NAME") || 'ชื่อระดับสมาชิก' }} <span
                      class="text-danger">*</span></h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input v-model="form.name" :state="nameState" placeholder="เช่น Bronze, Silver, Gold, Platinum, VIP"
                  aria-describedby="name-live-feedback"></b-form-input>
                <b-form-invalid-feedback id="name-live-feedback">
                  กรุณาระบุชื่อระดับสมาชิก
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">รูปภาพตราสัญลักษณ์</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <div class="d-flex align-items-center">
                  <div class="mr-4 d-flex align-items-center justify-content-center bg-light rounded"
                    style="width: 55px; height: 50px; min-width: 55px; border: 1px dashed #ccc; overflow: hidden;">
                    <img v-if="previewImage" :src="previewImage"
                      style="width: 100%; height: 100%; object-fit: contain;" />
                    <span v-else class="svg-icon svg-icon-lg svg-icon-muted">
                      <inline-svg src="media/svg/icons/Design/Image.svg" />
                    </span>
                  </div>
                  <div class="flex-grow-1">
                    <b-form-file ref="imageFileInput" v-model="form.image_file" @change="onFileChange" accept="image/png,image/jpeg,image/webp"
                      :placeholder="$t('PAGE.DEFAULT.FORM.PLACEHOLDER.CHOOSE_IMAGE') || 'เลือกรูปภาพสัญลักษณ์ (PNG, JPG, WebP)...'"
                      drop-placeholder="วางไฟล์ที่นี่..."></b-form-file>
                    <small class="text-muted d-block mt-1">
                      รองรับไฟล์ PNG / JPG / WebP ขนาดไม่เกิน 2 MB แนะนำภาพสี่เหลี่ยมจัตุรัส 512x512 พิกเซล
                    </small>
                    <span v-if="previewImage" class="text-danger font-size-sm cursor-pointer mt-1 d-inline-block"
                      @click="removeImage">
                      <span class="svg-icon svg-icon-xs svg-icon-danger mr-1">
                        <inline-svg src="media/svg/icons/General/Trash.svg" />
                      </span>
                      ลบรูปภาพ
                    </span>
                  </div>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">เกณฑ์คะแนนขั้นต่ำ <span class="text-danger">*</span></h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input v-model.number="form.min_points" type="number" :min="minPointFloor" placeholder="เช่น 500, 2000"
                  :disabled="isFirstTier" @change="keepMinPointAboveFloor"></b-form-input>
                <small v-if="isFirstTier" class="text-muted">
                  * ระดับแรกเริ่มที่ 0 แต้มเสมอ
                </small>
                <small v-else-if="!isEdit && previousTier" class="text-muted">
                  * ต้องมากกว่า {{ (minPointFloor - 1).toLocaleString() }} แต้ม ระบบจะปรับเกณฑ์คะแนนสูงสุดของระดับ {{ previousTier.name }} เป็นเกณฑ์ขั้นต่ำนี้ลบ 1 แต้มให้อัตโนมัติ
                </small>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">เกณฑ์คะแนนสูงสุด</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input v-if="maxIsUnlimited" value="ไม่จำกัด" disabled></b-form-input>
                <b-form-input v-else v-model.number="form.max_points" type="number" :min="form.min_points || 0"
                  placeholder="เช่น 499, 1999"></b-form-input>
                <small class="text-muted">
                  {{ maxIsUnlimited
                    ? '* ระดับสูงสุดไม่จำกัดเพดานคะแนนเสมอ สมาชิกที่มีคะแนนเกินเพดานจะยังอยู่ในระดับสูงสุด'
                    : '* ต้องต่อเนื่องกับเกณฑ์ขั้นต่ำของระดับถัดไป' }}
                </small>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">อัตราตัวคูณแต้มสะสม</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-select v-model="form.multiplier" :options="availableMultiplierOptions"></b-form-select>
                <small class="text-muted">* แต่ละระดับต้องใช้ตัวคูณไม่ซ้ำกัน และระดับที่สูงกว่าต้องมีตัวคูณมากกว่าระดับที่ต่ำกว่า</small>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3" class="d-flex align-items-center">
                <label class="mb-0">
                  <h5 class="mb-0">สีประจำระดับ (Tier Color)</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <div class="d-flex align-items-center">
                  <input type="color" v-model="form.color" class="form-control form-control-color mr-3"
                    style="width: 50px; height: 38px; padding: 2px; cursor: pointer;" />
                  <b-form-input v-model="form.color" placeholder="#3699FF" style="max-width: 120px;"
                    class="mr-3"></b-form-input>
                  <div class="d-flex">
                    <span v-for="color in presetColors" :key="color" class="rounded-circle mr-2" :style="{
                      backgroundColor: color,
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      border: form.color === color ? '2px solid #000' : '1px solid #ddd'
                    }" @click="form.color = color"></span>
                  </div>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="3">
                <label>
                  <h5>สิทธิประโยชน์</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <ckeditor :editor="editor" v-model="form.benefit_description" :config="editorConfig"></ckeditor>
              </b-col>
            </b-row>


            <b-row class="mt-8">
              <b-col sm="12">
                <b-button class="float-right font-weight-bold" @click.prevent="cancel()">
                  {{ $t("PAGE.DEFAULT.FORM.BTN_CANCEL") || 'ยกเลิก' }}
                </b-button>
                <b-button type="submit" variant="primary" class="float-right mr-3 font-weight-bold"
                  :disabled="isSaving || isLoadingTier || !hasChanges || previousTierIsOpenEnded">
                  {{ isSaving ? 'กำลังบันทึก...' : ($t("PAGE.DEFAULT.FORM.BTN_SUBMIT") || 'บันทึก') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";
import {
  allowedMultipliers,
  isTopTier,
  nextTierMinPoint,
  previewNeighborRangeChanges
} from "@/core/services/member-tier.helper";
import { Config } from "@/core/config/variable.config";
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat";
import AutoLink from "@ckeditor/ckeditor5-link/src/autolink";
import Autosave from "@ckeditor/ckeditor5-autosave/src/autosave";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import CloudServices from "@ckeditor/ckeditor5-cloud-services/src/cloudservices";
import Code from "@ckeditor/ckeditor5-basic-styles/src/code";
import CodeBlock from "@ckeditor/ckeditor5-code-block/src/codeblock";
import DataFilter from "@ckeditor/ckeditor5-html-support/src/datafilter";
import DataSchema from "@ckeditor/ckeditor5-html-support/src/dataschema";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import FindAndReplace from "@ckeditor/ckeditor5-find-and-replace/src/findandreplace";
import FontBackgroundColor from "@ckeditor/ckeditor5-font/src/fontbackgroundcolor";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor";
import FontFamily from "@ckeditor/ckeditor5-font/src/fontfamily";
import FontSize from "@ckeditor/ckeditor5-font/src/fontsize";
import GeneralHtmlSupport from "@ckeditor/ckeditor5-html-support/src/generalhtmlsupport";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import Highlight from "@ckeditor/ckeditor5-highlight/src/highlight";
import HorizontalLine from "@ckeditor/ckeditor5-horizontal-line/src/horizontalline";
import HtmlComment from "@ckeditor/ckeditor5-html-support/src/htmlcomment";
import Indent from "@ckeditor/ckeditor5-indent/src/indent";
import IndentBlock from "@ckeditor/ckeditor5-indent/src/indentblock";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Link from "@ckeditor/ckeditor5-link/src/link";
import List from "@ckeditor/ckeditor5-list/src/list";
import ListProperties from "@ckeditor/ckeditor5-list/src/listproperties";
import Mention from "@ckeditor/ckeditor5-mention/src/mention";
import PageBreak from "@ckeditor/ckeditor5-page-break/src/pagebreak";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import RemoveFormat from "@ckeditor/ckeditor5-remove-format/src/removeformat";
import SpecialCharacters from "@ckeditor/ckeditor5-special-characters/src/specialcharacters";
import SpecialCharactersArrows from "@ckeditor/ckeditor5-special-characters/src/specialcharactersarrows";
import SpecialCharactersCurrency from "@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency";
import SpecialCharactersEssentials from "@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials";
import SpecialCharactersLatin from "@ckeditor/ckeditor5-special-characters/src/specialcharacterslatin";
import SpecialCharactersMathematical from "@ckeditor/ckeditor5-special-characters/src/specialcharactersmathematical";
import SpecialCharactersText from "@ckeditor/ckeditor5-special-characters/src/specialcharacterstext";
import StandardEditingMode from "@ckeditor/ckeditor5-restricted-editing/src/standardeditingmode";
import Strikethrough from "@ckeditor/ckeditor5-basic-styles/src/strikethrough";
import Subscript from "@ckeditor/ckeditor5-basic-styles/src/subscript";
import Superscript from "@ckeditor/ckeditor5-basic-styles/src/superscript";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableCaption from "@ckeditor/ckeditor5-table/src/tablecaption";
import TableCellProperties from "@ckeditor/ckeditor5-table/src/tablecellproperties";
import TableProperties from "@ckeditor/ckeditor5-table/src/tableproperties";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import TextPartLanguage from "@ckeditor/ckeditor5-language/src/textpartlanguage";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation";
import TodoList from "@ckeditor/ckeditor5-list/src/todolist";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline";
import WordCount from "@ckeditor/ckeditor5-word-count/src/wordcount";
import EmojiActivity from "@/core/plugins/emoji-activity";
import EmojiFlags from "@/core/plugins/emoji-flags";
import EmojiFood from "@/core/plugins/emoji-food";
import EmojiNature from "@/core/plugins/emoji-nature";
import EmojiObjects from "@/core/plugins/emoji-objects";
import EmojiPeople from "@/core/plugins/emoji-people";
import EmojiPlaces from "@/core/plugins/emoji-places";
import EmojiSymbols from "@/core/plugins/emoji-symbols";

export default {
  name: "MemberTierForm",
  props: {
    companyId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      isEdit: false,
      tierId: null,
      isSaving: false,
      isLoadingTier: false,
      isSubmitted: false,
      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          Autoformat,
          AutoLink,
          Autosave,
          BlockQuote,
          Bold,
          CloudServices,
          Code,
          CodeBlock,
          DataFilter,
          DataSchema,
          Essentials,
          FindAndReplace,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          HtmlComment,
          Indent,
          IndentBlock,
          Italic,
          Link,
          List,
          ListProperties,
          Mention,
          PageBreak,
          Paragraph,
          PasteFromOffice,
          RemoveFormat,
          SpecialCharacters,
          EmojiPeople,
          EmojiFood,
          EmojiPlaces,
          EmojiActivity,
          EmojiFlags,
          EmojiNature,
          EmojiObjects,
          EmojiSymbols,
          SpecialCharactersArrows,
          SpecialCharactersCurrency,
          SpecialCharactersEssentials,
          SpecialCharactersLatin,
          SpecialCharactersMathematical,
          SpecialCharactersText,
          StandardEditingMode,
          Strikethrough,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableProperties,
          TableToolbar,
          TextPartLanguage,
          TextTransformation,
          TodoList,
          Underline,
          WordCount
        ],
        toolbar: {
          items: [
            "heading",
            "|",
            "bold",
            "underline",
            "italic",
            "strikethrough",
            "|",
            "fontSize",
            "fontColor",
            "fontBackgroundColor",
            "fontFamily",
            "|",
            "link",
            "insertTable",
            "specialCharacters",
            "|",
            "undo",
            "redo",
            "-",
            "horizontalLine",
            "pageBreak",
            "superscript",
            "subscript",
            "highlight",
            "|",
            "bulletedList",
            "numberedList",
            "todoList",
            "alignment",
            "outdent",
            "indent",
            "removeFormat",
            "blockQuote"
          ],
          shouldNotGroupWhenFull: true
        },
        language: "en",
        table: {
          contentToolbar: [
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableCellProperties",
            "tableProperties"
          ]
        }
      },
      benefitId: null,
      presetColors: [
        "#CD7F32",
        "#A8A9AD",
        "#D4AF37",
        "#4A5568",
        "#3699FF",
        "#8950FC"
      ],
      multiplierOptions: [
        { value: null, text: "เลือกอัตราตัวคูณ", disabled: true },
        ...Array.from({ length: 19 }, (value, index) => {
          const multiplier = 1 + index * 0.5;
          return { value: multiplier, text: String(multiplier) };
        })
      ],
      previewImage: null,
      imageBase64: null,
      imageRemoved: false,
      originalSnapshot: null,
      previousTier: null,
      minPointFloor: 0,
      tiers: [],
      form: {
        name: "",
        image_file: null,
        min_points: 0,
        max_points: null,
        multiplier: 1.0,
        color: "#3699FF",
        benefit_description: "",
        version_no: null
      }
    };
  },
  computed: {
    effectiveCompanyId() {
      return (
        this.companyId ||
        this.$route.params.companyId ||
        this.$route.query.companyId ||
        null
      );
    },
    returnRoute() {
      return this.effectiveCompanyId
        ? `/company-management/${this.effectiveCompanyId}/member-tier`
        : "/member-tier";
    },
    availableMultiplierOptions() {
      if (this.isEdit) {
        return this.multiplierOptions;
      }
      const allowed = allowedMultipliers(
        this.multiplierOptions
          .map((option) => option.value)
          .filter((value) => value !== null),
        this.tiers
      );
      return this.multiplierOptions.filter(
        (option) => option.value === null || allowed.includes(option.value)
      );
    },
    isFirstTier() {
      return !this.isEdit && !this.isLoadingTier && !this.previousTier;
    },
    maxIsUnlimited() {
      return !this.isEdit || isTopTier(this.tiers, this.tierId);
    },
    hasChanges() {
      if (!this.isEdit) {
        return true;
      }
      if (this.originalSnapshot === null) {
        return false;
      }
      if (this.imageBase64 || this.imageRemoved) {
        return true;
      }
      return this.buildSnapshot() !== this.originalSnapshot;
    },
    nameState() {
      if (!this.isSubmitted) {
        return null;
      }
      return this.form.name && this.form.name.trim() !== "" ? null : false;
    }
  },
  watch: {
    $route: {
      handler(to, from) {
        if (!from) return;
        const idChanged = to.params.id !== from.params.id;
        const companyParamChanged =
          to.params.companyId !== from.params.companyId;
        const companyQueryChanged =
          to.query.companyId !== from.query.companyId;
        const pathChanged = to.path !== from.path;
        if (
          idChanged ||
          companyParamChanged ||
          companyQueryChanged ||
          pathChanged
        ) {
          this.initPage();
        }
      }
    },
    companyId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initPage();
      }
    }
  },
  mounted() {
    this.initPage();
  },
  beforeDestroy() {
    if (
      this.previewImage &&
      typeof this.previewImage === "string" &&
      this.previewImage.startsWith("blob:")
    ) {
      URL.revokeObjectURL(this.previewImage);
    }
  },
  methods: {
    resetForm() {
      if (
        this.previewImage &&
        typeof this.previewImage === "string" &&
        this.previewImage.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.previewImage);
      }
      this.isSaving = false;
      this.isSubmitted = false;
      this.benefitId = null;
      this.previewImage = null;
      this.imageBase64 = null;
      this.imageRemoved = false;
      this.originalSnapshot = null;
      this.previousTier = null;
      this.minPointFloor = 0;
      this.tiers = [];
      this.form = {
        name: "",
        image_file: null,
        min_points: 0,
        max_points: null,
        multiplier: 1.0,
        color: "#3699FF",
        benefit_description: "",
        version_no: null
      };
    },
    initPage() {
      this.resetForm();
      this.tierId = this.$route.params.id || null;
      this.isEdit = !!this.tierId;

      const companyRoute = this.effectiveCompanyId
        ? "/company-management"
        : "/company";
      this.$store.dispatch(SET_BREADCRUMB, [
        {
          title: `${this.$t("MENU.COMPANY_MANAGEMENT.TITLE")}`,
          route: companyRoute
        },
        { title: "ระดับสมาชิก", route: this.returnRoute },
        { title: this.isEdit ? "แก้ไขระดับสมาชิก" : "เพิ่มระดับสมาชิก" }
      ]);

      if (this.isEdit) {
        this.loadTierData();
      }
      this.loadTiers();
    },
    buildSnapshot() {
      return JSON.stringify({
        name: (this.form.name || "").trim(),
        min_points: this.form.min_points,
        max_points: this.form.max_points === "" || this.form.max_points === undefined ? null : this.form.max_points,
        multiplier: this.form.multiplier,
        color: this.form.color,
        benefits: this.benefitsPayload()
      });
    },
    benefitsToHtml(benefits) {
      const isHtml = text =>
        /^<(p|ul|ol|h[1-6]|blockquote|figure|table|pre|hr|div)[\s>/]/i.test(text);
      const escapeHtml = text =>
        text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const descriptions = benefits
        .map(b => (b.description || "").trim())
        .filter(d => d !== "");

      if (descriptions.length > 1 && !descriptions.some(isHtml)) {
        return `<ul>${descriptions.map(d => `<li>${escapeHtml(d)}</li>`).join("")}</ul>`;
      }
      return descriptions
        .map(d => (isHtml(d) ? d : `<p>${escapeHtml(d)}</p>`))
        .join("");
    },
    benefitsPayload() {
      const html = (this.form.benefit_description || "").trim();
      const text = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
      const hasMedia = /<(img|figure|oembed|table|hr)[\s>/]/i.test(html);
      return text === "" && !hasMedia
        ? []
        : [{ id: this.benefitId, description: html }];
    },
    loadTiers() {
      this.isLoadingTier = true;
      ApiService.setHeader();
      const params = {};
      if (this.effectiveCompanyId) {
        params.company_id = this.effectiveCompanyId;
      }
      ApiService.getParams("member_tier", Object.keys(params).length ? { params } : undefined)
        .then(({ data }) => {
          if (!(data && data.status === "success" && data.data)) {
            return;
          }
          this.tiers = Array.isArray(data.data.items) ? data.data.items : [];
          if (!this.isEdit) {
            this.form.version_no = data.data.version_no;
            this.prefillMinPoints(this.tiers);
            this.prefillMultiplier();
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!this.isEdit) {
            this.isLoadingTier = false;
          }
        });
    },
    prefillMultiplier() {
      const first = this.availableMultiplierOptions.find(
        (option) => option.value !== null
      );
      this.form.multiplier = first ? first.value : null;
    },
    prefillMinPoints(tiers) {
      const { previousTier, minPointFloor } = nextTierMinPoint(tiers);
      this.previousTier = previousTier;
      this.minPointFloor = minPointFloor;
      this.form.min_points = previousTier ? null : 0;
    },
    keepMinPointAboveFloor() {
      if (this.form.min_points === "" || this.form.min_points === null) {
        return;
      }
      if (Number(this.form.min_points) < this.minPointFloor) {
        this.form.min_points = this.minPointFloor;
      }
    },
    loadTierData() {
      this.isLoadingTier = true;
      ApiService.setHeader();
      const currentTierId = this.tierId;
      const slug = this.effectiveCompanyId
        ? `${currentTierId}?company_id=${this.effectiveCompanyId}`
        : currentTierId;
      ApiService.get("member_tier", slug)
        .then(({ data }) => {
          if (this.tierId !== currentTierId) {
            return;
          }
          if (data && data.status === "success" && data.data) {
            const tier = data.data;
            this.form.name = tier.name;
            this.form.min_points = tier.min_point;
            this.form.max_points = tier.max_point;
            const multiplier = Number(tier.point_multiplier);
            this.form.multiplier = this.multiplierOptions.some(option => option.value === multiplier)
              ? multiplier
              : null;
            this.form.color = tier.color || "#3699FF";
            const benefits = Array.isArray(tier.benefits) ? tier.benefits : [];
            this.benefitId = benefits.length ? benefits[0].id : null;
            this.form.benefit_description = this.benefitsToHtml(benefits);
            this.form.version_no = tier.version_no;
            this.previewImage = tier.image
              ? `${Config.ADMIN_IMAGE_URL}/${tier.image}`
              : null;
            this.$nextTick(() => {
              if (this.tierId === currentTierId) {
                this.originalSnapshot = this.buildSnapshot();
              }
            });
          } else {
            Swal.fire({
              icon: "error",
              text: "ไม่พบข้อมูลระดับสมาชิกนี้",
              confirmButtonText: "ตกลง"
            }).then(() => this.$router.push(this.returnRoute));
          }
        })
        .catch(() => {
          if (this.tierId !== currentTierId) {
            return;
          }
          Swal.fire({
            icon: "error",
            text: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
            confirmButtonText: "ตกลง"
          });
        })
        .finally(() => {
          if (this.tierId === currentTierId) {
            this.isLoadingTier = false;
          }
        });
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) {
        return;
      }

      const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: "warning",
          text: "รองรับเฉพาะไฟล์รูปภาพ PNG, JPG หรือ WebP เท่านั้น",
          confirmButtonText: "ตกลง"
        }).then(() => this.$refs.imageFileInput.reset());
        return;
      }

      const maxSizeBytes = 2 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        Swal.fire({
          icon: "warning",
          text: "ขนาดไฟล์รูปภาพต้องไม่เกิน 2 MB",
          confirmButtonText: "ตกลง"
        }).then(() => this.$refs.imageFileInput.reset());
        return;
      }

      this.form.image_file = file;
      this.previewImage = URL.createObjectURL(file);

      this.imageRemoved = false;

      const reader = new FileReader();
      reader.onload = ev => {
        this.imageBase64 = ev.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeImage() {
      this.form.image_file = null;
      this.previewImage = null;
      this.imageBase64 = null;
      this.imageRemoved = true;
    },
    onSubmit() {
      if (this.isLoadingTier || !this.hasChanges) {
        return;
      }

      this.isSubmitted = true;

      if (!this.form.name || this.form.name.trim() === "") {
        return;
      }

      if (!this.isEdit && this.previousTier) {
        const hasMinPoint = this.form.min_points !== "" && this.form.min_points !== null;
        if (!hasMinPoint || Number(this.form.min_points) < this.minPointFloor) {
          Swal.fire({
            icon: "warning",
            text: hasMinPoint
              ? `เกณฑ์คะแนนขั้นต่ำต้องมากกว่า ${(this.minPointFloor - 1).toLocaleString()} แต้ม ซึ่งเป็นเกณฑ์ขั้นต่ำของระดับก่อนหน้า (${this.previousTier.name})`
              : "กรุณาระบุเกณฑ์คะแนนขั้นต่ำ",
            confirmButtonText: "ตกลง"
          });
          return;
        }
      }

      if (!this.maxIsUnlimited && this.form.max_points !== null && this.form.max_points !== "" && this.form.max_points < this.form.min_points) {
        Swal.fire({
          icon: "warning",
          text: "เกณฑ์คะแนนสูงสุดต้องไม่น้อยกว่าเกณฑ์คะแนนขั้นต่ำ",
          confirmButtonText: "ตกลง"
        });
        return;
      }

      if (this.form.multiplier === null) {
        Swal.fire({
          icon: "warning",
          text: "กรุณาเลือกอัตราตัวคูณแต้มสะสม",
          confirmButtonText: "ตกลง"
        });
        return;
      }

      const benefits = this.benefitsPayload();

      const payload = {
        name: this.form.name.trim(),
        min_point: this.form.min_points || 0,
        max_point:
          this.maxIsUnlimited || this.form.max_points === "" || this.form.max_points === undefined
            ? null
            : this.form.max_points,
        point_multiplier: this.form.multiplier,
        color: this.form.color,
        status_id: 1,
        benefits,
        version_no: this.form.version_no
      };

      if (this.effectiveCompanyId) {
        payload.company_id = this.effectiveCompanyId;
      }

      if (this.imageBase64) {
        payload.image = this.imageBase64;
      } else if (this.imageRemoved) {
        payload.image = null;
      }

      const executeSave = () => {
        if (this.isSaving) {
          return;
        }
        this.isSaving = true;
        ApiService.setHeader();

        const request = this.isEdit
          ? ApiService.update("member_tier", this.tierId, payload)
          : ApiService.post("member_tier", payload);

        request
          .then(({ data }) => {
            if (data && data.status === "success") {
              Swal.fire({
                icon: "success",
                text: this.isEdit ? "แก้ไขระดับสมาชิกเรียบร้อยแล้ว" : "เพิ่มระดับสมาชิกเรียบร้อยแล้ว",
                timer: 1500,
                showConfirmButton: false
              }).then(() => {
                this.$router.push(this.returnRoute);
              });
            } else {
              const code = data && data.error;
              if (code === "VERSION_CONFLICT") {
                Swal.fire({
                  icon: "warning",
                  text: "มีการแก้ไขข้อมูลระดับสมาชิกโดยผู้ใช้อื่นไปก่อนหน้านี้ กรุณาโหลดหน้านี้ใหม่แล้วลองอีกครั้ง",
                  confirmButtonText: "โหลดใหม่"
                }).then(() => {
                  this.$router.go(0);
                });
                return;
              }
              const messageByCode = {
                NAME_DUPLICATE: "มีชื่อระดับสมาชิกนี้อยู่แล้ว",
                TIER_POINT_GAP:
                  "ช่วงคะแนนของแต่ละระดับต้องต่อเนื่องกัน ไม่มีช่วงที่ไม่ตรงกับระดับใดเลย กรุณาตั้งเกณฑ์คะแนนสูงสุดของระดับก่อนหน้าให้น้อยกว่าเกณฑ์ขั้นต่ำของระดับถัดไป 1 แต้ม",
                TIER_TOP_MUST_BE_UNLIMITED:
                  "ระดับสูงสุดต้องเว้นเกณฑ์คะแนนสูงสุดไว้ (ไม่จำกัด) เพื่อให้ครอบคลุมคะแนนทุกช่วง",
                DATA_DUPLICATE: "ข้อมูลระดับสมาชิกซ้ำกับที่มีอยู่แล้ว"
              };
              const message =
                (data && data.message) ||
                messageByCode[code] ||
                (code && this.$te("RESPONSE_MESSAGE." + code)
                  ? this.$t("RESPONSE_MESSAGE." + code)
                  : null) ||
                "บันทึกข้อมูลไม่สำเร็จ";
              Swal.fire({ icon: "error", text: message, confirmButtonText: "ตกลง" });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              text: "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
              confirmButtonText: "ตกลง"
            });
          })
          .finally(() => {
            this.isSaving = false;
          });
      };

      const neighborChanges = previewNeighborRangeChanges(
        this.tiers,
        this.isEdit ? this.tierId : null,
        payload.min_point,
        payload.max_point
      );

      if (this.isEdit && neighborChanges.length === 0) {
        executeSave();
        return;
      }

      const confirmMessage = this.isEdit
        ? "การแก้ไขนี้จะปรับช่วงคะแนนของระดับข้างเคียงด้วย"
        : "ระดับสมาชิกเมื่อสร้างแล้วจะไม่สามารถลบได้ในภายหลัง";

      Swal.fire({
        title: this.isEdit ? "ยืนยันการแก้ไขช่วงคะแนน" : "ยืนยันการสร้างระดับสมาชิก",
        html: `${confirmMessage}${this.neighborChangesTableHtml(neighborChanges)}<br>คุณต้องการดำเนินการต่อหรือไม่?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      }).then((result) => {
        if (result.isConfirmed) {
          executeSave();
        }
      });
    },
    neighborChangesTableHtml(changes) {
      if (changes.length === 0) {
        return "<br>";
      }
      const escapeHtml = text =>
        String(text)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const formatRange = range =>
        `${range.min.toLocaleString()} – ${range.max === null ? "ไม่จำกัด" : range.max.toLocaleString()}`;
      const rows = changes
        .map(change => `
          <tr>
            <td class="text-left">${escapeHtml(change.tier.name)}</td>
            <td>${formatRange(change.before)}</td>
            <td class="font-weight-bold">${formatRange(change.after)}</td>
          </tr>`)
        .join("");
      return `
        <table class="table table-sm table-bordered text-center mt-4 mb-2">
          <thead>
            <tr>
              <th class="text-left">ระดับ</th>
              <th>ช่วงคะแนนเดิม</th>
              <th>ช่วงคะแนนใหม่</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>`;
    },
    cancel() {
      this.$router.push(this.returnRoute);
    }
  }
};
</script>

<style scoped>
* {
  font-family: SukhumvitSet, sans-serif !important;
}

.form-control-color {
  max-width: 50px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
